/**
 * In-game pause modal, opened from the persistent gear icon (per-app canvas)
 * or the global `Escape` key. Pure DOM — no canvas. Each app supplies its own
 * `GameOverlayContext` adapter; the overlay never touches gameplay state
 * directly.
 *
 * Two views, swapped inside the same modal card:
 *
 *  - **Main view** (default): Settings entry button, Restart, any
 *    game-specific extra actions, Return to menu, Give up, Close.
 *  - **Settings view**: full settings panel mirroring the main Settings
 *    screen, with a Back button returning to the main view.
 *
 * Construction does **not** open the modal — call `open()` to mount it. Each
 * `open()` creates a fresh DOM tree; `close()` removes it. Idempotent close.
 */

import { el } from "./components/dom";
import { createCrestButton } from "./components/CrestButton";
import { createSettingsPanel, type SettingsPanelHandle } from "./components/SettingsPanel";
import { confirmDialog } from "./dialogs";
import { installFocusTrap, type FocusTrapHandle } from "./focusTrap";
import type { GameOverlayContext, GameOverlayStrings } from "./types";

export interface GameOverlayOptions {
  readonly context: GameOverlayContext;
  readonly strings: GameOverlayStrings;
}

type OverlayView = "main" | "settings";

interface MainRefs {
  readonly view: "main";
  readonly restartButton: HTMLButtonElement;
  readonly restartHint: HTMLElement;
  /** Game-specific action buttons, by action id. */
  readonly extraButtons: ReadonlyMap<string, HTMLButtonElement>;
}

interface SettingsRefs {
  readonly view: "settings";
  readonly settingsPanel: SettingsPanelHandle;
}

type Refs = MainRefs | SettingsRefs;

let overlayTitleIdSeq = 0;

export class GameOverlay {
  private readonly opts: GameOverlayOptions;
  private overlayNode: HTMLElement | null = null;
  private titleNode: HTMLElement | null = null;
  private bodyNode: HTMLElement | null = null;
  private actionsNode: HTMLElement | null = null;
  private trap: FocusTrapHandle | null = null;
  private titleId: string | null = null;
  private refs: Refs | null = null;
  private view: OverlayView = "main";
  // Two-step confirm flow state: track which step we're in so a sequence of
  // No/Yes presses can't accidentally trigger the action before confirmation.
  private giveUpStep: "idle" | "step1" = "idle";
  private returnStep: "idle" | "step1" = "idle";

  constructor(opts: GameOverlayOptions) {
    this.opts = opts;
  }

  /** True iff the modal is currently mounted in the DOM. */
  isOpen(): boolean {
    return this.overlayNode !== null;
  }

  toggle(): void {
    if (this.isOpen()) this.close();
    else this.open();
  }

  open(): void {
    if (this.isOpen()) return;
    const overlay = el("div", { class: "modal-overlay" });
    this.titleId = `bn-game-overlay-title-${++overlayTitleIdSeq}`;
    const card = el("div", {
      class: "modal-card",
      attrs: {
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": this.titleId,
        tabindex: "-1",
      },
    });
    this.titleNode = el("h2", {
      class: "modal-title",
      text: "",
      attrs: { id: this.titleId },
    });
    card.appendChild(this.titleNode);

    this.bodyNode = el("div", { class: "modal-body game-overlay-body" });
    card.appendChild(this.bodyNode);

    this.actionsNode = el("div", { class: "modal-actions" });
    card.appendChild(this.actionsNode);

    overlay.appendChild(card);
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.close();
    });

    document.body.appendChild(overlay);
    this.overlayNode = overlay;
    this.view = "main";
    this.renderView();
    this.trap = installFocusTrap(card);
  }

  close(): void {
    if (this.trap !== null) {
      this.trap.release();
      this.trap = null;
    }
    if (this.overlayNode !== null) {
      this.overlayNode.remove();
      this.overlayNode = null;
      this.titleNode = null;
      this.bodyNode = null;
      this.actionsNode = null;
      this.titleId = null;
      this.refs = null;
    }
    this.view = "main";
    this.giveUpStep = "idle";
    this.returnStep = "idle";
  }

  /**
   * Re-read the restart state from the context and update the button + hint.
   * Call this when the overlay needs to react to wire events (`RESTART_PENDING`
   * / `RESTART_CANCELLED` / `RESTART_REJECTED`) without closing the modal.
   *
   * No-op while the settings sub-view is active — the restart button isn't
   * mounted there, so there's nothing to refresh.
   */
  refreshRestartState(): void {
    if (this.refs?.view !== "main") return;
    const { context, strings } = this.opts;
    const { pending, canPropose } = context.getRestartState();
    const online = context.isOnline();
    const disabled = online && (pending || !canPropose);
    this.refs.restartButton.disabled = disabled;
    if (!online) {
      this.refs.restartHint.textContent = "";
      return;
    }
    if (pending) {
      this.refs.restartHint.textContent = strings.restartHintPending;
    } else if (!canPropose) {
      this.refs.restartHint.textContent = strings.restartHintAlreadyProposed;
    } else {
      this.refs.restartHint.textContent = strings.restartHintOnline;
    }
  }

  /**
   * Re-read the game's extra actions and update their buttons' label and
   * enabled state in place (e.g. chess's draw offer becoming available again
   * after a move) without closing the modal. No-op in the settings sub-view.
   */
  refreshExtraActions(): void {
    if (this.refs?.view !== "main") return;
    for (const action of this.opts.context.getExtraActions?.() ?? []) {
      const button = this.refs.extraButtons.get(action.id);
      if (button === undefined) continue;
      button.disabled = !action.enabled;
      if (button.textContent !== action.label) button.textContent = action.label;
    }
  }

  /**
   * Force a re-render of the embedded settings panel. Use after applying a
   * setting change from outside the modal (e.g. a restart that mutated
   * settings.difficulty) so the row values stay accurate while the modal
   * remains open.
   */
  refreshSettings(): void {
    if (this.refs?.view === "settings") this.refs.settingsPanel.rerender();
  }

  private renderView(): void {
    if (this.bodyNode === null || this.actionsNode === null || this.titleNode === null) return;
    const { strings } = this.opts;
    while (this.bodyNode.firstChild) this.bodyNode.removeChild(this.bodyNode.firstChild);
    while (this.actionsNode.firstChild) this.actionsNode.removeChild(this.actionsNode.firstChild);

    if (this.view === "main") {
      this.titleNode.textContent = strings.title;
      this.renderMainView();
    } else {
      this.titleNode.textContent = strings.settingsViewTitle;
      this.renderSettingsView();
    }
  }

  private renderMainView(): void {
    if (this.bodyNode === null || this.actionsNode === null) return;
    const { context, strings } = this.opts;

    // ─── Settings entry ────────────────────────────────────────────
    const settingsButton = createCrestButton({
      label: strings.settingsLabel,
      small: true,
      onClick: () => {
        this.view = "settings";
        this.renderView();
      },
    });
    this.bodyNode.appendChild(makeActionRow(settingsButton));

    // ─── Restart match ─────────────────────────────────────────────
    const restartButton = createCrestButton({
      label: strings.restartLabel,
      small: true,
      onClick: () => context.requestRestart(),
    });
    const restartHint = el("div", {
      class: "game-overlay-section__hint game-overlay-restart-hint",
    });
    const restartSection = makeActionRow(restartButton);
    restartSection.appendChild(restartHint);
    this.bodyNode.appendChild(restartSection);

    // ─── Game-specific actions ─────────────────────────────────────
    const extraButtons = new Map<string, HTMLButtonElement>();
    for (const action of context.getExtraActions?.() ?? []) {
      const button = createCrestButton({
        label: action.label,
        small: true,
        onClick: () => action.onSelect(),
      });
      button.disabled = !action.enabled;
      extraButtons.set(action.id, button);
      this.bodyNode.appendChild(makeActionRow(button));
    }

    // ─── Return to main menu (with confirm) ────────────────────────
    const returnButton = createCrestButton({
      label: strings.returnToMenuLabel,
      small: true,
      onClick: () => {
        void this.runReturnFlow();
      },
    });
    this.bodyNode.appendChild(makeActionRow(returnButton));

    // ─── Give up (two-step confirm) ────────────────────────────────
    const giveUpButton = createCrestButton({
      label: strings.giveUpLabel,
      small: true,
      onClick: () => {
        void this.runGiveUpFlow();
      },
    });
    this.bodyNode.appendChild(makeActionRow(giveUpButton));

    // ─── Close button (footer) ─────────────────────────────────────
    this.actionsNode.appendChild(
      createCrestButton({
        label: strings.close,
        primary: true,
        small: true,
        onClick: () => this.close(),
      }),
    );

    this.refs = { view: "main", restartButton, restartHint, extraButtons };
    this.refreshRestartState();
  }

  private renderSettingsView(): void {
    if (this.bodyNode === null || this.actionsNode === null) return;
    const { context, strings } = this.opts;

    const settingsPanel = createSettingsPanel({
      getRows: () => context.getSettingsRows(),
      tabs: [
        { id: "gameplay", label: strings.settingsTabs.gameplay },
        { id: "interface", label: strings.settingsTabs.interface },
        { id: "audio", label: strings.settingsTabs.audio },
      ],
    });
    this.bodyNode.appendChild(settingsPanel.element);

    // Footer: Back (to main view) + Close.
    this.actionsNode.appendChild(
      createCrestButton({
        label: strings.settingsBackLabel,
        small: true,
        onClick: () => {
          this.view = "main";
          this.renderView();
        },
      }),
    );
    this.actionsNode.appendChild(
      createCrestButton({
        label: strings.close,
        primary: true,
        small: true,
        onClick: () => this.close(),
      }),
    );

    this.refs = { view: "settings", settingsPanel };
  }

  private async runGiveUpFlow(): Promise<void> {
    if (this.giveUpStep !== "idle") return;
    this.giveUpStep = "step1";
    const { strings, context } = this.opts;
    try {
      const ok = await confirmDialog({
        title: strings.giveUpConfirmTitle,
        message: "",
        confirmLabel: strings.giveUpConfirmYes,
        cancelLabel: strings.giveUpConfirmNo,
        danger: true,
      });
      if (ok) {
        this.close();
        context.requestResign();
      }
    } finally {
      this.giveUpStep = "idle";
    }
  }

  private async runReturnFlow(): Promise<void> {
    if (this.returnStep !== "idle") return;
    this.returnStep = "step1";
    const { strings, context } = this.opts;
    try {
      const ok = await confirmDialog({
        title: strings.returnToMenuConfirmTitle,
        message: "",
        confirmLabel: strings.returnToMenuConfirmYes,
        cancelLabel: strings.returnToMenuConfirmNo,
        danger: true,
      });
      if (ok) {
        this.close();
        context.exitToMainMenu();
      }
    } finally {
      this.returnStep = "idle";
    }
  }
}

// ─── DOM helpers ────────────────────────────────────────────────────

function makeActionRow(control: HTMLElement): HTMLElement {
  return el("div", { class: "game-overlay-section game-overlay-section--action" }, [control]);
}
